import type { TemplateFormField, TemplateFormFieldUpdate } from '@common/types/templateFormFields';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import {
    buildApsPrefillPresetField,
    suggestApsPrefillTagMappings,
    toApsPrefillMappableFields,
} from '@common/utils/apsPrefillTagMapping';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Divider, Drawer, Tab, Tabs, Typography } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { generateEightDigitsNumber } from '@/utils';
import FromScratchQuestionsTab, {
    type FromScratchQuestionsTabHandle,
} from './FromScratchQuestionsTab';
import PreconfiguredQuestionsTab from './PreconfiguredQuestionsTab';

const FROM_SCRATCH_TAB = 0;
const PRECONFIGURED_TAB = 1;

interface NewQuestionsDrawerProps {
    isOpen: boolean;
    previousQuestionOrder: number;
    formField?: TemplateFormFieldUpdate;
    formGroup?: number;
    formGroupTitle?: string;
    isLoading?: boolean;
    practiceArea?: string | null;
    matterType?: string | null;
    existingFields?: TemplateFormField[];
    isIntakeForm?: boolean;
    /** Question lives inside a contact block's `questions` rather than as a form field row. */
    isNestedQuestion?: boolean;
    language?: TemplateFormLanguage;
    onConfirm: (fieldData: TemplateFormFieldUpdate | TemplateFormFieldUpdate[]) => void;
    onClose: () => void;
}

export default function NewQuestionsDrawer({
    isOpen,
    formField,
    formGroup,
    formGroupTitle,
    previousQuestionOrder,
    isLoading,
    practiceArea,
    matterType,
    existingFields,
    isIntakeForm,
    isNestedQuestion,
    language,
    onClose,
    onConfirm,
}: NewQuestionsDrawerProps) {
    const scratchSubmitRef = useRef<FromScratchQuestionsTabHandle | null>(null);
    const [drawerTab, setDrawerTab] = useState(FROM_SCRATCH_TAB);
    const [selectedPresets, setSelectedPresets] = useState<string[]>([]);
    const [isScratchSubmitDisabled, setIsScratchSubmitDisabled] = useState(true);

    const showPreconfiguredTab =
        !!isIntakeForm &&
        !formField &&
        !isNestedQuestion &&
        (matterType === 'Purchase' || matterType === 'Sale');

    const presetQuestions = useMemo(() => {
        if (!showPreconfiguredTab) return [];
        return suggestApsPrefillTagMappings(
            toApsPrefillMappableFields(existingFields ?? []),
            matterType ?? '',
        ).unmappedTags;
    }, [existingFields, matterType, showPreconfiguredTab]);

    const existingFieldNames = useMemo(
        () => new Set((existingFields ?? []).map((field) => field.name)),
        [existingFields],
    );

    const allPresetsSelected =
        presetQuestions.length > 0 && selectedPresets.length === presetQuestions.length;

    const handleToggleAllPresets = () => {
        if (allPresetsSelected) {
            setSelectedPresets([]);
            return;
        }
        setSelectedPresets(presetQuestions.map((preset) => preset.tag));
    };

    const handleTogglePreset = (tag: string) => {
        setSelectedPresets((current) =>
            current.includes(tag) ? current.filter((value) => value !== tag) : [...current, tag],
        );
    };

    const handleAddPresets = () => {
        const fields = presetQuestions
            .filter((preset) => selectedPresets.includes(preset.tag))
            .map((preset, index) =>
                buildApsPrefillPresetField(preset, {
                    name: existingFieldNames.has(preset.tag)
                        ? `${preset.questionType}_${generateEightDigitsNumber()}`
                        : preset.tag,
                    formGroup,
                    formGroupTitle,
                    displayOrder: previousQuestionOrder + index + 1,
                }),
            );
        if (fields.length === 0) return;
        onConfirm(fields);
    };

    const handleClose = () => {
        setDrawerTab(FROM_SCRATCH_TAB);
        setSelectedPresets([]);
        setIsScratchSubmitDisabled(true);
        onClose();
    };

    const isPreconfiguredTab = showPreconfiguredTab && drawerTab === PRECONFIGURED_TAB;

    return (
        <Drawer anchor="right" open={isOpen} onClose={handleClose}>
            <Box
                sx={{
                    width: showPreconfiguredTab ? 880 : 750,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    height: '100%',
                }}
            >
                <Box>
                    <Box
                        sx={{
                            padding: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <Button
                            variant="text"
                            sx={{ width: 'fit-content', minWidth: 'auto' }}
                            onClick={handleClose}
                        >
                            <ArrowBackIcon sx={{ fontSize: 14 }} />
                        </Button>
                        <Typography variant="labelLarge" color="text.primary">
                            {formField ? 'Update question' : 'Add new question'}{' '}
                            {isNestedQuestion
                                ? 'for each entry'
                                : formGroupTitle
                                  ? `in ${formGroupTitle.toLowerCase()}`
                                  : ''}
                        </Typography>
                    </Box>
                    <Divider variant="fullWidth" />
                </Box>
                {showPreconfiguredTab && (
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: '16px' }}>
                        <Tabs
                            value={drawerTab}
                            onChange={(_event, value: number) => setDrawerTab(value)}
                            aria-label="add question tabs"
                        >
                            <Tab label="From scratch" sx={{ textTransform: 'none' }} />
                            <Tab label="Preconfigured" sx={{ textTransform: 'none' }} />
                        </Tabs>
                    </Box>
                )}
                <Box
                    sx={{
                        padding: '16px 28px',
                        flex: 1,
                        minHeight: 0,
                        overflow: isPreconfiguredTab ? 'hidden' : 'auto',
                        display: isPreconfiguredTab ? 'flex' : 'block',
                        flexDirection: 'column',
                    }}
                >
                    {isPreconfiguredTab ? (
                        <PreconfiguredQuestionsTab
                            presets={presetQuestions}
                            selected={selectedPresets}
                            onToggleAll={handleToggleAllPresets}
                            onToggleRow={handleTogglePreset}
                        />
                    ) : (
                        <FromScratchQuestionsTab
                            formField={formField}
                            formGroup={formGroup}
                            formGroupTitle={formGroupTitle}
                            previousQuestionOrder={previousQuestionOrder}
                            practiceArea={practiceArea}
                            matterType={matterType}
                            isIntakeForm={isIntakeForm}
                            isNestedQuestion={isNestedQuestion}
                            language={language}
                            submitRef={scratchSubmitRef}
                            onConfirm={onConfirm}
                            onSubmitDisabledChange={setIsScratchSubmitDisabled}
                        />
                    )}
                </Box>
                <Box>
                    <Divider variant="fullWidth" />
                    <Box
                        sx={{
                            padding: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: '8px',
                        }}
                    >
                        <Button variant="outlined" onClick={handleClose}>
                            Cancel
                        </Button>
                        {isPreconfiguredTab ? (
                            <LoadingButton
                                loading={isLoading}
                                disabled={selectedPresets.length === 0}
                                variant="contained"
                                onClick={handleAddPresets}
                            >
                                Add Questions
                            </LoadingButton>
                        ) : (
                            <LoadingButton
                                loading={isLoading}
                                disabled={isScratchSubmitDisabled}
                                variant="contained"
                                onClick={() => scratchSubmitRef.current?.submit()}
                            >
                                {formField ? 'Save Question' : 'Add Question'}
                            </LoadingButton>
                        )}
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
}
